import { useQuery } from '@tanstack/react-query'
import { useEnv } from '../context'
import { useSportFields } from '../data/hooks'
import type { SportOption } from '../data/types'
import { Field } from './Form'
import { Skeleton } from './States'

/** Справочник видов спорта: мастер и экраны направлений. */
export function useSportTypes() {
  const { api } = useEnv()
  return useQuery({ queryKey: ['redesign', 'sport-types'], queryFn: () => api.listSportTypes(), staleTime: 30 * 60_000 })
}

/** Выбор вида спорта из справочника (мастер S02 и направления вне мастера). */
export function SportSelect({
  sports,
  value,
  onChange,
  label = 'Вид спорта',
}: {
  sports: SportOption[]
  value: string
  onChange: (name: string) => void
  label?: string
}) {
  // Вид спорта направления, которого нет в справочнике (записан прежним
  // интерфейсом свободным текстом), остаётся видимым и выбранным.
  const known = sports.some((s) => s.name === value)
  return (
    <Field label={label}>
      {(p) => (
        <select {...p} className="pc-input" value={value} onChange={(e) => onChange(e.target.value)}>
          {!value && <option value="">Выберите</option>}
          {value && !known && <option value={value}>{value}</option>}
          {sports.map((s) => (
            <option key={s.name} value={s.name}>
              {s.name}
            </option>
          ))}
        </select>
      )}
    </Field>
  )
}

/**
 * Какие поля даст вид спорта в карточке спортсмена - до создания или смены
 * (S02: пользователь видит, что получит, до заполнения карточек).
 */
export function SportFieldsPreview({ sportName, title = 'Поля карточки спортсмена' }: { sportName: string; title?: string }) {
  const fields = useSportFields(sportName || undefined)
  return (
    <section aria-label={title}>
      <h2 className="pc-section-title">{title}</h2>
      {fields.isPending ? (
        <Skeleton rows={1} />
      ) : (fields.data ?? []).length === 0 ? (
        <p className="pc-muted" style={{ margin: 0, fontSize: 14 }}>
          Для этого вида спорта отдельных полей нет: будут только общие (имя, группа, контакты, измерения).
        </p>
      ) : (
        <ul className="pc-list pc-card" style={{ padding: '0 12px' }}>
          {(fields.data ?? []).map((f) => (
            <li key={f.field_key}>
              <div className="pc-kv">
                <div className="pc-kv__label">{f.field_label}</div>
                <div className="pc-kv__value">
                  {f.field_type === 'select' && f.field_options?.length
                    ? `Выбор: ${f.field_options.slice(0, 4).join(', ')}${f.field_options.length > 4 ? '…' : ''}`
                    : 'Заполняется в карточке'}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
